import { TKAsyncContent } from '@tk/components/tk.async.content';
import type { PayloadItem, PayloadItemData } from '@tk/utilities/tk.async.content.manager';

export interface PriceRequest {
    addrNo?: number;
    articles: string;
    dimensionVariants: string;
    quantities: string;
}

export interface GrossSalPrices {
    GrossSalPrice: string;
    GrossSalPrice_Stan: string;
    GrossSalPrice_Spec: string;
    GrossSalPrice_Prom: string;
}

export interface NetPrices {
    NetSalPrice: string;
    NetSalPrice_Stan: string;
    NetSalPrice_Spec: string;
    NetSalPrice_Prom: string;
}

export interface VatPrices {
    NetSalPriceVat: string;
    NetSalPriceVat_Stan: string;
    NetSalPriceVat_Spec: string;
    NetSalPriceVat_Prom: string;
}

export interface InclPrices {
    NetSalPriceInclVat: string;
    NetSalPriceInclVat_Stan: string;
    NetSalPriceInclVat_Spec: string;
    NetSalPriceInclVat_Prom: string;
}

export interface ExclPrices {
    NetSalPriceExclVat: string;
    NetSalPriceExclVat_Stan: string;
    NetSalPriceExclVat_Spec: string;
    NetSalPriceExclVat_Prom: string;
}

export interface PriceResult {
    articleId: string;
    currency: string;
    grossSalPrices: GrossSalPrices;
    netPrices: NetPrices;
    vatPrices: VatPrices;
    inclPrices: InclPrices;
    exclPrices: ExclPrices;
}

export interface PriceResponse {
    usecase: 'price';
    length: number;
    results: PriceResult[];
}

/**
 * `<tk-async-price>` — Web Component for asynchronous price loading.
 *
 * Extends the generic `TKAsyncContent` component with price-specific defaults
 * and a TSX-based render method.
 *
 * Required attributes:
 *   data-tk-id  — Unique article identifier (Art.InternalNo)
 *   data-tk-url — API endpoint URL (e.g. from gCreateURL(API.lc¦50))
 *
 * Optional attributes (with defaults):
 *   data-tk-usecase        — "price" (default)
 *   data-tk-cache-strategy — "session" (default)
 *   data-tk-is-lazy        — present = use Intersection Observer (default: absent)
 *   data-tk-root-margin    — "200px" (default)
 *   data-tk-threshold      — 0 (default)
 *
 * Usage:
 *   <tk-async-price data-tk-id="123" data-tk-url="/api/prices" data-tk-is-lazy></tk-async-price>
 */
export default class TKPrice extends TKAsyncContent {
    override get url(): string | null {
        const globalUrl = document.querySelector<HTMLElement>('[data-tk-price-api]')?.getAttribute('data-tk-url');
        return globalUrl ?? super.url;
    }

    connectedCallback(): void {
        if (!this.hasAttribute('data-tk-usecase')) {
            this.setAttribute('data-tk-usecase', 'price');
        }
        if (!this.hasAttribute('data-tk-cache-strategy')) {
            this.setAttribute('data-tk-cache-strategy', 'session');
        }
        super.connectedCallback();
    }

    // ─── Price-specific request params ───────────────────────────────────────

    /**
     * Returns the per-item data that varies between product instances:
     * dimensionVariant and quantity.
     * These values are batched together with data from other tk-async-price
     * elements and forwarded to `buildPayload`.
     */
    protected override getItemData(): PayloadItemData {
        const itemData = {
            dimensionVariant: this.getAttribute('data-tk-dimension-variant') ?? '0',
            quantity: this.getAttribute('data-tk-quantity') ?? '1',
        };
        return itemData;
    }

    /**
     * Assembles the Price API POST body from all batched items.
     * Produces comma-separated lists of articles, dimensionVariants, and quantities
     * in the same order, as required by the Opacc PriceRequest schema.
     */
    protected override buildPayload(items: PayloadItem[]): Record<string, unknown> {
        return {
            usecase: 'price',
            articles: items.map((i) => i.id).join(','),
            dimensionVariants: items.map((i) => String(i.itemData?.['dimensionVariant'] ?? '0')).join(','),
            quantities: items.map((i) => String(i.itemData?.['quantity'] ?? '1')).join(','),
        };
    }

    public render(data: unknown): string {
        const priceData = data as PriceResult;
        const inclPrices = priceData?.inclPrices;

        if (!inclPrices) {
            return <div class="tk-price tk-price--error" />;
        }

        const basePrice = parseFloat(inclPrices.NetSalPriceInclVat);
        const customPrice = parseFloat(inclPrices.NetSalPriceInclVat_Spec);
        const promotionalPrice = parseFloat(inclPrices.NetSalPriceInclVat_Prom);
        const basePriceFormatted = isNaN(basePrice) ? '' : basePrice.toFixed(2);

        const hasPromotion = !isNaN(promotionalPrice) && promotionalPrice > 0;
        const hasCustom = !isNaN(customPrice) && customPrice > 0;
        const displayPrice = hasPromotion ? promotionalPrice : hasCustom ? customPrice : basePrice;
        const displayPriceFormatted = isNaN(displayPrice) ? '' : displayPrice.toFixed(2);
        const basePriceLabel = this.getTranslation('base_price_label');
        const promotionPriceLabel = this.getTranslation('promotion_price_label');

        const rootClass = {
            'tk-price': true,
            'tk-price--has-custom': hasCustom,
            'tk-price--has-promotion': hasPromotion,
        };

        return (
            <div class={rootClass}>
                <div class="tk-price__basic">
                    {basePriceLabel && <span class="tk-price__label">{basePriceLabel}</span>}
                    <span class="tk-price__currency">{priceData?.currency}</span>
                    <span class="tk-price__value">{basePriceFormatted}</span>
                </div>
                {hasPromotion && (
                    <div class="tk-price__promotion">
                        {promotionPriceLabel && <span class="tk-price__label">{promotionPriceLabel}</span>}
                        <span class="tk-price__currency">{priceData?.currency}</span>
                        <span class="tk-price__value">{displayPriceFormatted}</span>
                    </div>
                )}
            </div>
        );
    }
}
